import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import {
  UsersThree, ArrowsClockwise, Crown, ShieldCheck, User, ArrowUp, ArrowDown,
  CaretRight, CheckCircle, X, Sparkle, ChartBar, PlayCircle, Exam, ChatCircle,
  MagnifyingGlass, Code, UserCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useRoleLabel, useAuth } from "../contexts/AuthContext";
import { getCanonicalRoles } from "../lib/canonicalRoles";

import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";

// Vincent #216 (2026-02-06) — Suppression des descriptions inventées
// ("Membre standard de l'équipe", "Directeur / expert", etc.) qui ne
// correspondaient à rien dans la Gestion des rôles. On garde l'identifiant
// technique comme description, source unique alignée sur le backend.
const TIER_VISUALS = {
  viewer:            { access: 10,  color: "zinc-400",     icon: UserCircle,  desc: "viewer" },
  employee:          { access: 25,  color: "blue-400",     icon: User,        desc: "employee" },
  employee_new:      { access: 30,  color: "sky-500",      icon: User,        desc: "employee_new" },
  employee_trained:  { access: 45,  color: "teal-500",     icon: User,        desc: "employee_trained" },
  employee_advanced: { access: 65,  color: "cyan-500",     icon: User,        desc: "employee_advanced" },
  concepteur:        { access: 75,  color: "violet-500",   icon: ShieldCheck, desc: "concepteur" },
  admin:             { access: 90,  color: "amber-500",    icon: ShieldCheck, desc: "admin" },
  founder:           { access: 100, color: "emerald-500",  icon: Crown,       desc: "founder" },
};

const buildTiers = (user, lang) => getCanonicalRoles(user, lang).map((r) => {
  const v = TIER_VISUALS[r.id] || TIER_VISUALS.employee;
  return { id: r.id, label: r.label, level: null, access: v.access, color: v.color, icon: v.icon, desc: v.desc };
});

export default function TierManagementPage() {
  // Tâche 1387 — les libellés de rôles suivent la langue de l'écran.
  const { tr, lang } = useLang();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null);
  // Vincent #JS-NO-ACCESS (Eric 2026-05-27) — État pour le modal de reset mdp
  const [pwdResetModal, setPwdResetModal] = useState(null);
  // Vincent #267 (2026-02-06) — État pour le modal d'édition de fiche membre.
  const [editProfileTarget, setEditProfileTarget] = useState(null);
  const [editProfileForm, setEditProfileForm] = useState({ name: "", email: "" });
  // Vincent #276 (Eric 2026-02-06) — État pour le modal "Gérer les coupons de ce user"
  const [couponsTarget, setCouponsTarget] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const openEditProfile = (member) => {
    setEditProfileTarget(member);
    setEditProfileForm({ name: member.name || "", email: member.email || "" });
  };

  const saveProfile = async () => {
    if (!editProfileTarget) return;
    const payload = {};
    if (editProfileForm.name.trim() && editProfileForm.name !== editProfileTarget.name) {
      payload.name = editProfileForm.name.trim();
    }
    if (editProfileForm.email.trim() && editProfileForm.email.toLowerCase() !== (editProfileTarget.email || "").toLowerCase()) {
      payload.email = editProfileForm.email.trim().toLowerCase();
    }
    if (Object.keys(payload).length === 0) {
      toast.info(tr("Aucun changement", "No change"));
      setEditProfileTarget(null);
      return;
    }
    setSavingProfile(true);
    try {
      await axios.put(
        `${API}/api/organizations/members/${editProfileTarget.id}/profile`,
        payload,
        { withCredentials: true }
      );
      toast.success(tr("Fiche mise à jour", "Record updated"));
      setEditProfileTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setSavingProfile(false);
    }
  };

  // Génère un mot de passe temporaire fort et lisible
  const generateTempPassword = () => {
    const digits = Math.floor(1000 + Math.random() * 9000);
    return `Knoria-${digits}!`;
  };

  const resetMemberPassword = async (member) => {
    const ok = await confirmDialog(
      `Réinitialiser le mot de passe de ${member.name || member.email} ?\n\nUn nouveau mot de passe temporaire va être généré. Vous devrez le communiquer manuellement à ${member.email}.\n\nLe membre devra le changer à sa prochaine connexion.`
    );
    if (!ok) return;
    const newPwd = generateTempPassword();
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/platform/admin-set-user-access`,
        { email: member.email, new_password: newPwd },
        { withCredentials: true }
      );
      setPwdResetModal({ member, newPwd });
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur lors du reset", "Reset failed"));
    } finally {
      setBusy(false);
    }
  };
  const [busy, setBusy] = useState(false);
  const TIERS = buildTiers(user, lang);
  const TIER_BY_ID = TIERS.reduce((acc, t) => ({ ...acc, [t.id]: t }), {});

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/team-tiers`, { withCredentials: true });
      setMembers(r.data.members || []);
    } catch (e) {
      toast.error(tr("Erreur de chargement", "Loading failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const promoteOrDemote = async (member, direction) => {
    // Vincent 2026-06-02 — Ordre canonique des employés (escalade).
    const order = ["employee_new", "employee_trained", "employee_advanced"];
    const currentIdx = order.indexOf(member.tier);
    if (currentIdx === -1) return;
    const newIdx = direction === "up" ? currentIdx + 1 : currentIdx - 1;
    if (newIdx < 0 || newIdx >= order.length) return;
    const newTier = TIER_BY_ID[order[newIdx]];
    setConfirmTarget({ member, newTier, direction });
  };

  const confirmPromotion = async () => {
    if (!confirmTarget) return;
    setBusy(true);
    try {
      // Vincent XLSX 2026-07-15 11h30 Row 46217 #12 — Utilise l'endpoint
      // unifié /tier qui met à jour role + org_role + access_level +
      // is_founder + is_designer de manière cohérente.
      await axios.put(
        `${API}/api/platform/users/${confirmTarget.member.id}/tier`,
        { tier: confirmTarget.newTier.id },
        { withCredentials: true }
      );
      toast.success(
        confirmTarget.direction === "up"
          ? `${confirmTarget.member.name} promu vers ${confirmTarget.newTier.label}`
          : `${confirmTarget.member.name} rétrogradé vers ${confirmTarget.newTier.label}`
      );
      setConfirmTarget(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur", "Error"));
    } finally {
      setBusy(false);
    }
  };

  // Vincent 2026-06-02 — Modification directe via dropdown : passe par l'endpoint
  // /access-level pour les rangs employés progressifs et /role pour les autres.
  const changeTierDirectly = async (member, newTierId) => {
    if (newTierId === member.tier) return;
    const newTier = TIER_BY_ID[newTierId];
    if (!newTier) return;
    const ok = await confirmDialog(
      `Changer le rôle de ${member.name || member.email} :\n\n  ${TIER_BY_ID[member.tier]?.label || member.tier} → ${newTier.label}\n\nCette action modifie immédiatement les permissions de l'utilisateur.`
    );
    if (!ok) {
      return;
    }
    setBusy(true);
    try {
      // Vincent XLSX 2026-07-15 11h30 Row 46217 #12 (Eric 2026-07-15 SPEC STRICK) —
      // BUG CRITIQUE FIXÉ : appel unique vers /tier qui met à jour role +
      // org_role + access_level + is_founder + is_designer de manière
      // cohérente. Auparavant on appelait /access-level OU /role, qui ne
      // mettaient QU'UNE PARTIE des champs à jour → le calcul du tier
      // (basé sur org_role) restait figé sur l'ancienne valeur.
      await axios.put(
        `${API}/api/platform/users/${member.id}/tier`,
        { tier: newTierId },
        { withCredentials: true }
      );
      toast.success(`Rôle modifié : ${newTier.label}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || tr("Erreur lors du changement de rôle", "Role change failed"));
    } finally {
      setBusy(false);
    }
  };

  const filtered = members.filter((m) => {
    if (filter !== "all" && m.tier !== filter) return false;
    if (search && !`${m.name} ${m.email}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const tierCounts = members.reduce((acc, m) => ({ ...acc, [m.tier]: (acc[m.tier] || 0) + 1 }), {});

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
            <UsersThree size={22} weight="duotone" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="tier-mgmt-title">
              {tr("Gestion des tiers", "Tier management")} <Sparkle size={16} weight="fill" className="text-amber-400" />
            </h1>
            <p className="text-xs text-muted-foreground">
              {tr("Promotion manuelle des conseillers — vous décidez quand chacun progresse dans sa carrière.", "Manual promotion of advisors — you decide when each one moves up in their career.")}
            </p>
          </div>
        </div>
        <Button onClick={load} variant="outline" size="sm" className="gap-2" data-testid="btn-reload-team">
          <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> {tr("Actualiser", "Refresh")}
        </Button>
      </div>

      {/* Tier overview cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {TIERS.map((t) => {
          const Icon = t.icon;
          const count = tierCounts[t.id] || 0;
          const active = filter === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setFilter(active ? "all" : t.id)}
              className={`text-left bg-card border-2 rounded-xl p-4 transition-all hover:shadow-md ${
                active ? `border-${t.color}` : "border-border"
              }`}
              data-testid={`tier-filter-${t.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon size={20} weight="duotone" className={`text-${t.color}`} />
                {/* Vincent #259 (2026-02-06) — Pourcentage retiré (non pertinent
                    depuis l'introduction de la Sélection des accès matricielle). */}
              </div>
              <div className="text-2xl font-bold" data-testid={`tier-count-${t.id}`}>{count}</div>
              <div className="text-xs font-semibold">{t.label}</div>
              <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{t.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Search + filter reset */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr("Rechercher un conseiller...", "Search for an advisor…")}
            className="w-full pl-9 pr-3 h-9 bg-card border border-border rounded-lg text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
            data-testid="search-team-input"
          />
        </div>
        {filter !== "all" && (
          <button onClick={() => setFilter("all")} className="text-xs text-[#002FA7] hover:underline" data-testid="clear-filter">
            {tr("Tout afficher", "Show all")}
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} membre{filtered.length > 1 ? "s" : ""}
        </span>
      </div>

      {/* Members list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          <ArrowsClockwise size={28} className="mx-auto mb-2 animate-spin opacity-50" />
          {tr("Chargement…", "Loading…")}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-card border border-border rounded-2xl">
          <UsersThree size={42} weight="duotone" className="text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm font-semibold">{tr("Aucun membre", "No member")}</p>
          <p className="text-xs text-muted-foreground mt-1">{tr("Modifiez la recherche ou le filtre.", "Change the search or the filter.")}</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {filtered.map((m, idx) => {
            const tier = TIER_BY_ID[m.tier];
            const Icon = tier?.icon || User;
            const movable = ["employee_new", "employee_trained", "employee_advanced"].includes(m.tier);
            const canPromote = m.tier === "employee_new" || m.tier === "employee_trained";
            const canDemote = m.tier === "employee_trained" || m.tier === "employee_advanced";
            return (
              <div
                key={m.id}
                className={`flex items-center gap-4 p-4 hover:bg-secondary/40 transition-colors ${
                  idx > 0 ? "border-t border-border" : ""
                }`}
                data-testid={`member-row-${m.id}`}
              >
                {/* Avatar + identity */}
                <div className={`flex-shrink-0 w-11 h-11 rounded-xl bg-${tier?.color || "secondary"}/10 flex items-center justify-center`}>
                  <Icon size={20} weight="duotone" className={`text-${tier?.color || "muted-foreground"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{m.name || tr("Sans nom", "No name")}</span>
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-${tier?.color || "muted"}/10 text-${tier?.color || "muted-foreground"}`}>
                      {tier?.label}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                </div>

                {/* Progression stats */}
                <div className="hidden md:flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5" title={tr("Vidéos complétées", "Videos completed")}>
                    <PlayCircle size={14} className="text-blue-500" />
                    <span className="font-semibold">{m.stats?.videos_completed}/{m.stats?.videos_total || 0}</span>
                    <span className="text-muted-foreground">({m.stats?.progress_pct || 0}%)</span>
                  </div>
                  <div className="flex items-center gap-1.5" title={tr("Score moyen aux quiz", "Average quiz score")}>
                    <Exam size={14} className="text-amber-500" />
                    <span className="font-semibold">{m.stats?.avg_quiz_score || 0}%</span>
                    <span className="text-muted-foreground">({m.stats?.quiz_attempts || 0})</span>
                  </div>
                  <div className="flex items-center gap-1.5" title={tr("Requêtes chatbot", "Chatbot queries")}>
                    <ChatCircle size={14} className="text-violet-500" />
                    <span className="font-semibold">{m.stats?.chats_total || 0}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Vincent #266 (2026-02-06) — Boutons "Promouvoir" et
                      "Rétrograder" RETIRÉS. Le dropdown ci-dessous couvre 100%
                      des cas (admin peut affecter n'importe quel rôle), donc
                      les boutons "progressifs" étaient redondants.
                      Précédent : #167 (Eric 2026-05-26). */}
                  <select
                    value={m.tier}
                    onChange={(e) => changeTierDirectly(m, e.target.value)}
                    disabled={busy}
                    className="text-xs border border-zinc-300 rounded px-2 py-1.5 bg-white hover:border-zinc-400 disabled:opacity-50"
                    data-testid={`role-select-${m.id}`}
                    title={tr("Modifier directement le rôle", "Change the role directly")}
                  >
                    {TIERS.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  {/* Vincent #267 (2026-02-06) — Bouton éditer la fiche du membre
                      (nom + courriel). Modal léger qui appelle PUT /api/organizations/members/{id}/profile.
                      Les coupons restent gérés via la page Coupons existante. */}
                  <Button
                    onClick={() => openEditProfile(m)}
                    variant="outline"
                    size="sm"
                    className="gap-1 h-8 text-xs border-zinc-300 text-[#002FA7] hover:bg-blue-50"
                    data-testid={`btn-edit-profile-${m.id}`}
                    title={tr("Éditer la fiche (nom, courriel)", "Edit the record (name, email)")}
                  >
                    ✏️ {tr("Fiche", "Record")}
                  </Button>
                  {/* Vincent #276 (Eric 2026-02-06) — Bouton Coupons : ouvre un
                      modal qui liste TOUS les coupons de l'org avec une case
                      à cocher par coupon (attribué ou non à ce user). */}
                  <Button
                    onClick={() => setCouponsTarget(m)}
                    variant="outline"
                    size="sm"
                    className="gap-1 h-8 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid={`btn-edit-coupons-${m.id}`}
                    title={tr("Gérer les coupons de cet utilisateur", "Manage this user's coupons")}
                  >
                    🎟️ {tr("Coupons", "Coupons")}
                  </Button>
                  {/* Vincent #JS-NO-ACCESS (Eric 2026-05-27) — Bouton "Réinitialiser
                      le mot de passe" pour les fondateurs ROOT_OWNER. Permet de
                      débloquer un membre qui a oublié/perdu son mot de passe sans
                      avoir à appeler Emergent. Génère un mot de passe temporaire
                      affiché en clair UNE SEULE FOIS dans une modal. */}
                  {(user?.platform_role === "ROOT_OWNER") && (
                    <Button
                      onClick={() => resetMemberPassword(m)}
                      variant="outline"
                      size="sm"
                      className="gap-1 h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`btn-reset-pwd-${m.id}`}
                      title={tr("Réinitialiser le mot de passe (envoyer un nouveau au membre)", "Reset the password (send a new one to the member)")}
                    >
                      🔑 Reset MDP
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirmation modal */}
      {confirmTarget && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !busy && setConfirmTarget(null)}
        >
          <div
            className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="promotion-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                {confirmTarget.direction === "up" ? (
                  <><ArrowUp size={20} weight="bold" className="text-teal-600" /> {tr("Confirmer la promotion", "Confirm the promotion")}</>
                ) : (
                  <><ArrowDown size={20} className="text-amber-600" /> {tr("Confirmer la rétrogradation", "Confirm the demotion")}</>
                )}
              </h3>
              <button onClick={() => !busy && setConfirmTarget(null)} className="text-muted-foreground hover:text-foreground" data-testid="close-modal">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm mb-4">
              Vous êtes sur le point de {confirmTarget.direction === "up" ? "promouvoir" : "rétrograder"}{" "}
              <strong>{confirmTarget.member.name}</strong>.
            </p>
            <div className="bg-secondary/50 rounded-lg p-4 space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tier actuel :</span>
                <span className="font-semibold">{TIER_BY_ID[confirmTarget.member.tier]?.label}</span>
              </div>
              <div className="flex items-center justify-center text-muted-foreground">
                <CaretRight size={20} />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{tr("Nouveau tier :", "New tier:")}</span>
                <span className={`font-semibold text-${confirmTarget.newTier.color}`}>
                  {confirmTarget.newTier.label}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground pt-2 border-t border-border">
                {confirmTarget.newTier.desc}
              </div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-300 rounded-lg p-3 text-xs mb-4">
              <strong>📌 Rappel :</strong> Cette décision est manuelle. Vincent / Eric décident quand un conseiller a acquis assez d'expérience pour accéder à plus de fonctionnalités. La progression statistique est un indicateur, pas un déclencheur automatique.
            </div>
            <div className="flex items-center gap-2 justify-end">
              <Button onClick={() => setConfirmTarget(null)} disabled={busy} variant="outline" data-testid="btn-cancel-promotion">
                {tr("Annuler", "Cancel")}
              </Button>
              <Button
                onClick={confirmPromotion}
                disabled={busy}
                className={confirmTarget.direction === "up" ? "bg-teal-600 hover:bg-teal-700 text-white" : "bg-amber-600 hover:bg-amber-700 text-white"}
                data-testid="btn-confirm-promotion"
              >
                {busy ? <ArrowsClockwise size={14} className="animate-spin mr-1.5" /> : <CheckCircle size={14} weight="fill" className="mr-1.5" />}
                Confirmer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Vincent #JS-NO-ACCESS (Eric 2026-05-27) — Modal d'affichage du
          nouveau mot de passe temporaire après reset. UN SEUL affichage. */}
      {pwdResetModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPwdResetModal(null)}
        >
          <div
            className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="pwd-reset-modal"
          >
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              {tr("🔑 Mot de passe réinitialisé", "🔑 Password reset")}
            </h2>
            <div className="text-sm mb-4">
              <p className="mb-2">
                Nouveau mot de passe temporaire pour <strong>{pwdResetModal.member.name || pwdResetModal.member.email}</strong> :
              </p>
              <div className="bg-amber-50 border border-amber-300 rounded p-3 font-mono text-base text-amber-900 break-all select-all" data-testid="pwd-reset-value">
                {pwdResetModal.newPwd}
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                ⚠️ Ce mot de passe ne sera PAS affiché à nouveau. Copiez-le maintenant et transmettez-le par un canal sécurisé (téléphone, message chiffré). Le membre devra le changer à sa prochaine connexion.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(pwdResetModal.newPwd);
                    toast.success(tr("Mot de passe copié", "Password copied"));
                  } catch {
                    toast.error(tr("Impossible de copier — sélectionnez manuellement", "Could not copy — select it by hand"));
                  }
                }}
                variant="outline"
                data-testid="pwd-reset-copy"
              >
                Copier
              </Button>
              <Button
                onClick={() => setPwdResetModal(null)}
                className="bg-[#002FA7] hover:bg-[#001a5c] text-white"
                data-testid="pwd-reset-close"
              >
                {tr("J'ai copié, fermer", "Copied, close")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Vincent #267 (2026-02-06) — Modal édition fiche membre (nom + courriel) */}
      {editProfileTarget && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => !savingProfile && setEditProfileTarget(null)}
        >
          <div
            className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="edit-profile-modal"
          >
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
              {tr("✏️ Éditer la fiche", "✏️ Edit the record")}
            </h2>
            <p className="text-xs text-zinc-500 mb-4">
              Membre : <strong>{editProfileTarget.email}</strong>
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-zinc-700 mb-1 block">Nom complet</label>
                <input
                  type="text"
                  value={editProfileForm.name}
                  onChange={(e) => setEditProfileForm({ ...editProfileForm, name: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="edit-profile-name"
                  placeholder={tr("Prénom Nom", "First name Last name")}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-700 mb-1 block">Courriel</label>
                <input
                  type="email"
                  value={editProfileForm.email}
                  onChange={(e) => setEditProfileForm({ ...editProfileForm, email: e.target.value })}
                  className="w-full h-10 px-3 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="edit-profile-email"
                  placeholder="prenom.nom@exemple.com"
                />
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                ℹ️ {tr("Les coupons de rabais se gèrent depuis le menu ", "Discount coupons are managed from the ")}<strong>{tr("Coupons", "Coupons")}</strong>{tr(" (assignation, révocation).", " menu (granting, revoking).")}
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <Button
                onClick={() => setEditProfileTarget(null)}
                variant="outline"
                disabled={savingProfile}
                data-testid="edit-profile-cancel"
              >
                {tr("Annuler", "Cancel")}
              </Button>
              <Button
                onClick={saveProfile}
                disabled={savingProfile}
                className="bg-[#002FA7] hover:bg-[#001a5c] text-white"
                data-testid="edit-profile-save"
              >
                {savingProfile ? tr("Sauvegarde…", "Saving…") : tr("Enregistrer", "Save")}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Vincent #276 (Eric 2026-02-06) — Modal "Gérer les coupons de ce user" */}
      {couponsTarget && (
        <UserCouponsModal
          member={couponsTarget}
          onClose={() => setCouponsTarget(null)}
        />
      )}
    </div>
  );
}

function UserCouponsModal({ member, onClose }) {
  // Tâche 1387 — Sous-composant : il a son propre hook, il n'hérite pas du
  // `tr` de la page qui le contient.
  const { tr } = useLang();
  const [allCoupons, setAllCoupons] = useState([]);
  const [userCouponIds, setUserCouponIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [allRes, mineRes] = await Promise.all([
          axios.get(`${API}/api/coupons`, { withCredentials: true }),
          axios.get(`${API}/api/users/${member.id}/coupons`, { withCredentials: true }),
        ]);
        setAllCoupons(allRes.data?.coupons || allRes.data || []);
        const ids = new Set((mineRes.data?.coupons || []).map(c => c.id));
        setUserCouponIds(ids);
      } catch (e) {
        toast.error(e.response?.data?.detail || tr("Erreur de chargement", "Loading failed"));
      } finally {
        setLoading(false);
      }
    })();
  }, [member.id]);
  const toggle = (cid) => {
    setUserCouponIds(prev => {
      const n = new Set(prev);
      if (n.has(cid)) n.delete(cid); else n.add(cid);
      return n;
    });
  };
  const save = async () => {
    setSaving(true);
    let added = 0, removed = 0, failed = 0;
    for (const c of allCoupons) {
      const shouldHave = userCouponIds.has(c.id);
      try {
        // Récupère l'état actuel pour ce coupon
        const r = await axios.get(`${API}/api/coupons/${c.id}/user-grants`, { withCredentials: true });
        const grants = r.data?.grants || [];
        const isCurrentlyGranted = grants.some(g => g.user_id === member.id && g.is_granted);
        if (shouldHave === isCurrentlyGranted) continue;
        const newGrants = grants.filter(g => g.is_granted).map(g => ({ user_id: g.user_id, duration_months: g.duration_months_override }));
        if (shouldHave && !isCurrentlyGranted) {
          newGrants.push({ user_id: member.id, duration_months: null });
          added++;
        }
        if (!shouldHave && isCurrentlyGranted) {
          const idx = newGrants.findIndex(g => g.user_id === member.id);
          if (idx !== -1) newGrants.splice(idx, 1);
          removed++;
        }
        await axios.put(`${API}/api/coupons/${c.id}/user-grants`, { grants: newGrants }, { withCredentials: true });
      } catch {
        failed++;
      }
    }
    toast.success(tr(
      `Coupons mis à jour : +${added} attribué(s), -${removed} retiré(s)${failed ? `, ${failed} erreur(s)` : ""}`,
      `Coupons updated: +${added} granted, -${removed} removed${failed ? `, ${failed} error(s)` : ""}`,
    ));
    setSaving(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="user-coupons-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="user-coupons-modal">
        <div className="px-5 py-4 border-b">
          <h2 className="font-bold text-lg">🎟️ {tr("Coupons de", "Coupons for")} {member.name || member.email}</h2>
          <p className="text-xs text-zinc-500 mt-0.5">{tr("Coche pour attribuer, décoche pour retirer.", "Tick to grant, untick to remove.")}</p>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? <p className="text-sm text-zinc-400 text-center py-8">{tr("Chargement…", "Loading…")}</p> : allCoupons.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-8">{tr("Aucun coupon dans l'organisation.", "No coupon in the organisation.")}</p>
          ) : (
            <ul className="space-y-1.5">
              {allCoupons.map(c => {
                const checked = userCouponIds.has(c.id);
                return (
                  <li key={c.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${checked ? "border-violet-300 bg-violet-50" : "border-zinc-200"}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(c.id)}
                      className="h-4 w-4 rounded border-violet-400 text-violet-600"
                      data-testid={`user-coupon-toggle-${c.code}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{c.code} — {c.name}</div>
                      <div className="text-[11px] text-zinc-500">
                        {c.discount_percent}% {tr("de rabais", "off")}
                        {c.duration_months
                          ? tr(` · ${c.duration_months} mois`, ` · ${c.duration_months} month(s)`)
                          : c.duration_weeks
                            ? tr(` · ${c.duration_weeks} semaines`, ` · ${c.duration_weeks} week(s)`)
                            : tr(" · illimité", " · unlimited")}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-zinc-50 flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" size="sm" data-testid="user-coupons-cancel">{tr("Annuler", "Cancel")}</Button>
          <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" size="sm" data-testid="user-coupons-save">
            {saving ? tr("Sauvegarde…", "Saving…") : tr("Enregistrer", "Save")}
          </Button>
        </div>
      </div>
    </div>
  );
}
